import { useOrganization } from 'core/hooks/useOrganization';
import SelectCustomizable from 'core/modules/atoms/SelectCustomizable';
import * as coreEntities from 'core/store/index';
import './_education-year-selector.scss';
import { getEducationLevelLabel, getEducationYearsLabel } from 'core/utils/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

const EducationYearSelector = React.forwardRef(function EducationYearSelectorComponent(
  { t, value = {}, onChange, required = false, showLabel = true, disabled = false, name = 'education-year-selector' },
  ref
) {
  const { client } = useOrganization();
  const dispatch = useDispatch();

  // Get education data from Redux store
  const educationLevels = useSelector((state) => coreEntities.educationLevels.selectors.getEducationLevels(state)) || [];

  // Load education levels if not already loaded
  useEffect(() => {
    if (!educationLevels || educationLevels.length === 0) {
      dispatch(coreEntities.educationLevels.actions.getEducationLevels({ client }));
    }
  }, [dispatch, client]);

  // Flatten education levels and years into single array of options
  const options = useMemo(() => {
    if (!educationLevels || educationLevels.length === 0) return [];

    // Sort education levels by their order property from the API (order comes as string)
    const sortedLevels = [...educationLevels].sort((a, b) => parseInt(a.order ?? '0', 10) - parseInt(b.order ?? '0', 10));
    const levelLabels = getEducationLevelLabel(sortedLevels, t, client);

    return levelLabels.flatMap((level, levelIndex) => {
      const sortedLevel = sortedLevels[levelIndex];
      if (!sortedLevel.educationYears) return [];

      // Sort education years by order, then by year number
      const sortedYears = [...sortedLevel.educationYears].sort((a, b) => {
        const orderA = a.order ?? 0;
        const orderB = b.order ?? 0;
        if (orderA !== orderB) return orderA - orderB;

        // Sort by numeric value extracted from year label (e.g., "6°" → 6)
        const yearNumA = parseInt(a.year, 10) || 0;
        const yearNumB = parseInt(b.year, 10) || 0;
        return yearNumA - yearNumB;
      });

      const yearLabels = getEducationYearsLabel(sortedYears, t, client);

      return yearLabels.map((year, yearIndex) => ({
        label: `${year.year} ${level.level}`,
        value: JSON.stringify({
          educationLevel: sortedLevel.guid,
          educationYear: sortedYears[yearIndex].guid,
          educationLevelLabel: level.level,
          educationYearLabel: year.year,
        }),
      }));
    });
  }, [educationLevels, t, client]);

  function handleChange(data) {
    if (!data || !data.value) {
      onChange({});
      return;
    }

    const parsedValue = JSON.parse(data.value);

    // Find the actual level and year objects to get labels
    const level = educationLevels.find((l) => l.guid === parsedValue.educationLevel);
    const year = level?.educationYears?.find((y) => y.guid === parsedValue.educationYear);

    // Get labels with client-specific logic
    const levelLabels = level ? getEducationLevelLabel([level], t, client) : [];
    const yearLabels = year ? getEducationYearsLabel([year], t, client) : [];

    onChange({
      educationLevel: parsedValue.educationLevel,
      educationYear: parsedValue.educationYear,
      educationLevelLabel: levelLabels[0]?.level || parsedValue.educationLevelLabel || '',
      educationYearLabel: yearLabels[0]?.year || parsedValue.educationYearLabel || '',
    });
  }

  const selectedValue = useMemo(() => {
    if (!value.educationLevel || !value.educationYear) return null;

    return options.find((opt) => {
      try {
        const parsed = JSON.parse(opt.value);
        return parsed.educationLevel === value.educationLevel && parsed.educationYear === value.educationYear;
      } catch {
        return false;
      }
    });
  }, [value, options]);

  return (
    <div className="education-year-selector" ref={ref}>
      <SelectCustomizable
        options={options}
        name={name}
        label={showLabel ? t('chatbot:Education level') : null}
        placeholder={t('chatbot:Select level')}
        onChange={handleChange}
        value={selectedValue}
        isSearchable={false}
        required={required}
        isDisabled={disabled}
      />
    </div>
  );
});

EducationYearSelector.propTypes = {
  t: PropTypes.func.isRequired,
  value: PropTypes.shape({
    educationLevel: PropTypes.string,
    educationYear: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  showLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  name: PropTypes.string,
};

export default withTranslation(['chatbot', 'education-levels', 'education-years'])(EducationYearSelector);
